import { FC, useEffect, useMemo, useRef, useState } from 'react';
import ExitSlipFullDetailModal from './ExitSlipSnapshotFullDetailModal';
import { PreloadedQuery, usePreloadedQuery, useMutation } from 'react-relay';
import { ExitSlipClassExitSlipsQuery } from '../../../graphQLOperations/__generated__/ExitSlipClassExitSlipsQuery.graphql';
import {
  addClassExitSlip,
  cancelClassExitSlipById,
  deleteClassExitSlipByFilter,
  getAllClassExitSlip,
  getClassExitSlipsByFilter,
} from '../../../graphQLOperations/ExitSlip';
import ExitSlipSnapshotCardHeader from './ExitSlipSnapshotCardHeader';
import ExitSlipSnapshotCardBody from './ExitSlipSnapshotCardBody';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import {
  ExitSlipCascadeMove,
  ExitSlipRescheduleRow,
  describeExitSlipCancel,
  describeExitSlipMove,
  getExitSlipSchedule,
  getUpcomingExitSlip,
  groupByCurriculumLesson,
  lessonName,
  shortDay,
} from '../../../businessLogicService/exitSlipSnapshotHelper';
import {
  ClassExitSlip,
  FullDetailModalData,
  LearnosityReportData,
  LessonData,
} from '../../../custom-hooks/useExitSlipChartValues';
import ExitSlipSnapshotBanner from './ExitSlipSnapshotBanner';
import { isTokenExpired } from '../../../utils/isTokenExpired';
import { ExitSlipModalType, setExitSlipModal } from '../../../redux/reducers/exitSlipModalSlice';
import { selectUser } from '../../../redux/reducers/userSlice';
import { PrintModalData } from '../../ExitSlip/ExitSlipModalBodyData';
import { Skill } from '../../AssessmentHistory/models';
import ExitSlipPrintModal from '../../ExitSlip/ExitSlipPrintModal';
import SingleUnitSkillDialog from '../../SingleUnit/SingleUnitSkillDialog';
import { ExitSlipAllClassExitSlipQuery } from '../../../graphQLOperations/__generated__/ExitSlipAllClassExitSlipQuery.graphql';
import { selectedTeacherDashboardFilter } from '../../../redux/reducers/teacherDashboardFiltersSlice';
import { ExitSlipUpsertClassExitSlipMutation } from '../../../graphQLOperations/__generated__/ExitSlipUpsertClassExitSlipMutation.graphql';
import { ExitSlipDeleteClassExitSlipMutation } from '../../../graphQLOperations/__generated__/ExitSlipDeleteClassExitSlipMutation.graphql';
import { ExitSlipCancelClassExitSlipMutation } from '../../../graphQLOperations/__generated__/ExitSlipCancelClassExitSlipMutation.graphql';
import ExitSlipSnapshotRescheduleModal from './ExitSlipSnapshotRescheduleModal';
import { ExitSlipCancelTodayConfirm } from '../../ExitSlip/ExitSlipCancelConfirm';
import ExitSlipSnapshotMoveToast, { ExitSlipMoveToastHandle } from './ExitSlipSnapshotMoveToast';
import { Toast } from 'primereact/toast';
import { format, isToday } from 'date-fns';

interface Props {
  queryExitSlipRef: PreloadedQuery<ExitSlipClassExitSlipsQuery, Record<string, unknown>>;
  queryAllExitSlipRef: PreloadedQuery<ExitSlipAllClassExitSlipQuery, Record<string, unknown>>;
  dateExitSlip: Date;
  setDateExitSlip: (d: Date) => void;
}

export interface SkillModalData {
  visible: boolean;
  skill: Skill | null;
}

//which ticket is being edited, not just which action — several cards can offer the menu at once
type EditAssignmentAction = { action: 'reschedule' | 'cancel'; slipId: string };

const ExitSlipSnapshot: FC<Props> = ({ queryExitSlipRef, queryAllExitSlipRef, dateExitSlip, setDateExitSlip }) => {
  const dispatch = useAppDispatch();
  const userRedux = useAppSelector(selectUser);
  const teacherDashboardFilters = useAppSelector(selectedTeacherDashboardFilter);
  const toast = useRef<Toast>(null);

  const [editAction, setEditAction] = useState<EditAssignmentAction | null>(null);
  const [canceledTitle, setCanceledTitle] = useState('');
  const [saving, setSaving] = useState(false);
  const moveToast = useRef<ExitSlipMoveToastHandle>(null);
  const [nextLessonData, setNextLessonData] = useState<LessonData | null>(null);
  const [lessonsData, setLessonsData] = useState<LessonData[]>([]);
  const [classExitSlips, setClassExitSlips] = useState<ClassExitSlip[]>([]);
  const [fullDetailModalData, setFullDetailModalData] = useState<FullDetailModalData>({
    visible: false,
    title: '',
  });
  const [skillModalData, setSkillModalData] = useState<SkillModalData>({
    skill: null,
    visible: false,
  });
  const [printModalData, setPrintModalData] = useState<PrintModalData>({
    date: '',
    title: '',
    activityId: undefined,
    visible: false,
    fullScreenButton: true,
  });
  const [reportData, setReportData] = useState<LearnosityReportData>({
    activityId: '',
    userId: userRedux?.sub || '',
    reportId: '',
    reportType: '',
    userIds: [],
    sessionIds: [],
  });

  const data = usePreloadedQuery(getClassExitSlipsByFilter, queryExitSlipRef);
  const allLessons = usePreloadedQuery(getAllClassExitSlip, queryAllExitSlipRef);

  const [upsertClassExitSlip] = useMutation<ExitSlipUpsertClassExitSlipMutation>(addClassExitSlip);
  const [deleteClassExitSlip] = useMutation<ExitSlipDeleteClassExitSlipMutation>(deleteClassExitSlipByFilter);
  const [cancelClassExitSlip] = useMutation<ExitSlipCancelClassExitSlipMutation>(cancelClassExitSlipById);

  const handleTriggerModal = (selectedType: ExitSlipModalType) => {
    if (!isTokenExpired()) {
      dispatch(setExitSlipModal({ visible: true, type: selectedType }));
    } else {
      //preventing loop of queries
      window.location.reload();
    }
  };

  //TEAMB-3485 one schedule per class, so every card is managed against its own class's timeline
  const schedulesByClass = useMemo(() => {
    const classIds = new Set(allLessons.classExitSlips.map(slip => slip.class?.id).filter((id): id is string => !!id));
    return new Map([...classIds].map(id => [id, getExitSlipSchedule(allLessons, id)]));
  }, [allLessons]);

  //a card groups by lesson, so it is only unambiguously managed when exactly one class fed it
  const manageable = (slip: ClassExitSlip | undefined) => {
    //a canceled ticket has nothing left to reschedule or cancel — its only action is Re-assign,
    //which lives in the side panel
    if (!slip || slip.canceledOn || !isToday(dateExitSlip) || slip.class.length !== 1) return null;
    const klass = slip.class[0];
    const schedule = schedulesByClass.get(klass.id);
    const movingIndex = schedule?.upcoming.findIndex(row => row.classExitSlipId === slip.id) ?? -1;
    if (!schedule || movingIndex < 0) return null;
    return { slip, classId: klass.id, className: klass.name ?? '', schedule, movingIndex };
  };

  const multiClass = (teacherDashboardFilters.classes?.length ?? 0) > 1;
  const showsEditAssignment = (slip: ClassExitSlip) =>
    multiClass ? !slip.canceledOn && isToday(dateExitSlip) : !!manageable(slip);

  const active = editAction ? manageable(classExitSlips.find(slip => slip.id === editAction.slipId)) : null;
  const todaysTitle = active?.schedule.upcoming[active.movingIndex]?.title ?? '';
  const responseCount = active?.slip.learnosityExitSlipSessions.length ?? 0;

  const refetchSnapshot = () => dispatch(setExitSlipModal({ visible: false, exitSlipKey: new Date().getTime() }));

  const showError = () => {
    //both toasts sit bottom-center, so an error would otherwise stack on the move toast
    moveToast.current?.clear();
    toast.current?.show({
      severity: 'error',
      summary: 'Error',
      detail: 'Something went wrong. Please try again',
    });
  };

  const toInput = (classId: string) => (row: ExitSlipRescheduleRow, date: Date) => ({
    id: row.classExitSlipId,
    curriculumLessonId: row.curriculumLessonId,
    classId,
    dueOn: format(date, 'yyyy-MM-dd'),
  });

  const runMove = (classExitSlipInputs: ReturnType<ReturnType<typeof toInput>>[], onSuccess: () => void) => {
    setSaving(true);
    upsertClassExitSlip({
      variables: { input: { classExitSlipInputs } },
      onCompleted: response => {
        setSaving(false);
        if (response.upsertClassExitSlip.errors?.length) {
          showError();
          refetchSnapshot();
          return;
        }
        refetchSnapshot();
        onSuccess();
      },
      onError: () => {
        setSaving(false);
        showError();
        refetchSnapshot();
      },
    });
  };

  const handleRescheduleConfirm = (target: Date, moves: ExitSlipCascadeMove[], replaced?: ExitSlipRescheduleRow) => {
    if (!active) return;
    const schedule = active.schedule.upcoming;
    const mover = schedule[active.movingIndex];
    if (!mover) return;

    const input = toInput(active.classId);
    const inputs = [input(mover, target), ...moves.map(move => input(schedule[move.index], move.to))];

    const summary = describeExitSlipMove(mover.title, target, moves, replaced?.title ?? '');
    const applyMove = () =>
      runMove(inputs, () => {
        setEditAction(null);
        moveToast.current?.show(summary);
      });

    if (!replaced) {
      applyMove();
      return;
    }

    //the mover lands on the replaced ticket's day, so that row has to be gone before the upsert
    setSaving(true);
    deleteClassExitSlip({
      variables: { input: { classExitSlipId: replaced.classExitSlipId } },
      onCompleted: response => {
        if (response.deleteClassExitSlip.errors?.length) {
          setSaving(false);
          showError();
          return;
        }
        applyMove();
      },
      onError: () => {
        setSaving(false);
        showError();
      },
    });
  };

  const handleCancelConfirm = () => {
    if (!active) return;
    //TEAMB-3483 the backend keeps a taken ticket and marks it canceled, and only deletes one
    //nobody answered — so what to say afterwards depends on whether there were responses
    const kept = responseCount > 0;
    setSaving(true);
    cancelClassExitSlip({
      variables: { input: { classExitSlipId: active.slip.id } },
      onCompleted: response => {
        setSaving(false);
        if (response.cancelClassExitSlip.errors?.length) {
          showError();
          return;
        }
        setEditAction(null);
        //the empty-state line only appears when the card is gone, which is the deleted case
        if (!kept) setCanceledTitle(lessonName(todaysTitle));
        refetchSnapshot();
        moveToast.current?.show(describeExitSlipCancel(todaysTitle, responseCount));
      },
      onError: () => {
        setSaving(false);
        showError();
      },
    });
  };

  useEffect(() => {
    const parsedData = groupByCurriculumLesson(data);
    setClassExitSlips(parsedData as ClassExitSlip[]);
  }, [data]);

  useEffect(() => setCanceledTitle(''), [teacherDashboardFilters.classes]);

  useEffect(() => {
    setNextLessonData(getUpcomingExitSlip(allLessons));
    const pastLessonsOrder = allLessons.classExitSlips
      .map(el => {
        return {
          id: el.id ?? '',
          dueOn: el.dueOn ?? '',
          canceledOn: el.canceledOn ?? null,
          curriculumLesson: { name: el.curriculumLesson?.name ?? '', number: el.curriculumLesson?.number ?? '' },
        };
      })
      .sort((a, b) =>
        a.curriculumLesson.number.localeCompare(b.curriculumLesson.number, undefined, { sensitivity: 'base' }),
      );

    setLessonsData(pastLessonsOrder);
  }, [allLessons]);

  return (
    <>
      <div className="w-full mb-4 tw-flex tw-flex-col tw-items-start tw-justify-center tw-gap-5 tw-py-6 tw-px-8 tw-shadow-none tw-bg-white tw-border-[#F1F0EE] tw-border-4 tw-border-solid tw-rounded-2xl">
        <ExitSlipSnapshotCardHeader
          dateExitSlip={dateExitSlip}
          setDateExitSlip={setDateExitSlip}
          lessonsData={lessonsData}
        />
        {classExitSlips &&
          classExitSlips.map(classExitSlip => {
            return (
              <ExitSlipSnapshotCardBody
                key={classExitSlip.id}
                exitSlipValues={classExitSlip as unknown as ClassExitSlip}
                setFullDetailModalData={setFullDetailModalData}
                setSkillModalData={setSkillModalData}
                setPrintModalData={setPrintModalData}
                reportData={reportData}
                setReportData={setReportData}
                dateExitSlip={dateExitSlip}
                canEditAssignment={showsEditAssignment(classExitSlip)}
                editAssignmentDisabled={multiClass}
                onReschedule={() => setEditAction({ action: 'reschedule', slipId: classExitSlip.id })}
                onCancelAssignment={() => setEditAction({ action: 'cancel', slipId: classExitSlip.id })}
              />
            );
          })}
        {classExitSlips.length === 0 && (
          <div className="tw-flex tw-w-full tw-flex-col tw-items-center tw-justify-center tw-gap-2 tw-rounded-2xl tw-border-2 tw-border-solid tw-border-[#F1F0EE] tw-bg-[#F9F8F5] tw-h-24 tw-p-5 tw-font-atkinson">
            <span className="tw-text-sm tw-font-bold tw-leading-5 tw-text-[#686868]">
              {isToday(dateExitSlip) ? 'No exit ticket today' : `No exit ticket on ${shortDay(dateExitSlip)}`}
            </span>
            {!!canceledTitle && isToday(dateExitSlip) && (
              <span className="tw-text-center tw-text-sm tw-leading-5 tw-text-[#686868] [text-wrap:pretty]">
                {canceledTitle} was canceled for today
              </span>
            )}
          </div>
        )}
        <ExitSlipSnapshotBanner
          nextLesson={nextLessonData}
          classExitSlipsHasRecords={data.classExitSlips.length > 0}
          handleTriggerModal={handleTriggerModal}
        />
      </div>
      {fullDetailModalData.visible && (
        <ExitSlipFullDetailModal
          setFullDetailModalData={setFullDetailModalData}
          fullDetailModalData={fullDetailModalData}
          reportData={reportData}
        />
      )}
      {printModalData.visible && (
        <ExitSlipPrintModal setPrintModalData={setPrintModalData} printModalData={printModalData} />
      )}
      {skillModalData?.visible && skillModalData.skill && (
        <SingleUnitSkillDialog
          visible={skillModalData.visible}
          setVisible={(v: boolean) =>
            setSkillModalData(prev => (prev ? { ...prev, visible: v } : { visible: v, skill: null }))
          }
          skill={skillModalData.skill}
        />
      )}
      {active && editAction?.action === 'reschedule' && (
        <ExitSlipSnapshotRescheduleModal
          schedule={active.schedule.upcoming}
          pastTags={active.schedule.past}
          blocked={active.schedule.blocked}
          canceled={active.schedule.canceled}
          movingIndex={active.movingIndex}
          className={active.className}
          responseCount={responseCount}
          saving={saving}
          onClose={() => setEditAction(null)}
          onConfirm={handleRescheduleConfirm}
        />
      )}
      {active && editAction?.action === 'cancel' && (
        <ExitSlipCancelTodayConfirm
          className={active.className}
          responseCount={responseCount}
          saving={saving}
          onKeep={() => setEditAction(null)}
          onConfirm={handleCancelConfirm}
        />
      )}
      <ExitSlipSnapshotMoveToast ref={moveToast} />
      <Toast ref={toast} position="bottom-center" appendTo={document.body} />
    </>
  );
};

export default ExitSlipSnapshot;
